import { Navigate, useLocation } from 'react-router-dom';
import { useAuthentication } from '@/common-hooks/use-authentication';
import type { ProtectedRouteProperties } from '@/types/application/common/components/protected-route/protected-route';
import { PERMISSIONS } from '@/common-constants/permissions';

/** Returns the first route the user can access, so redirects don't send them to a page that will redirect again. */
function getDefaultRedirectPath(hasPermission: (p: string) => boolean): string {
  if (hasPermission(PERMISSIONS.READ)) return '/';
  if (hasPermission(PERMISSIONS.TRANSACTIONS_IN_LOG_ORACLE)) return '/log-oracle/transaction-lookup';
  if (hasPermission(PERMISSIONS.JOBS_IN_LOG_ORACLE)) return '/log-oracle/jobs';
  if (hasPermission(PERMISSIONS.R_KB_IN_LOG_ORACLE)) return '/log-oracle/ai-kb';
  if (hasPermission(PERMISSIONS.USER_MANAGEMENT)) return '/users';
  if (hasPermission(PERMISSIONS.AUDIT_LOGS)) return '/audit';
  return '/';
}

export default function ProtectedRoute({ permission, children }: ProtectedRouteProperties) {
  const { hasPermission, loading } = useAuthentication();
  const location = useLocation();

  if (loading) {
    return null;
  }

  const allowed = permission
    ? Array.isArray(permission)
      ? permission.some((p) => hasPermission(p))
      : hasPermission(permission)
    : hasPermission('read');

  if (!allowed) {
    const redirectTo = getDefaultRedirectPath(hasPermission);
    return <Navigate to={redirectTo} replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
